import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { TrackedChange } from 'models/changes'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react'
import { getContentItemViewNodeElementMapping } from '../../../utils/TrackChangesPanelUtils'
import { tabIndex, TabName } from './useHandleActiveTab'
import { CONTENT_FETCH_STATES } from './useScrollRequestExecutor'

/**
 * Hook for handling latest content item loading.
 *
 * Coordinates with tab switching to prevent race conditions by resetting
 * the coordination flag when content loading is complete.
 *
 * @param setIsReadyForContentFetch - State setter to signal when content loading is complete
 */
export const useHandleLatestContentItem = (
    setIsReadyForContentFetch: Dispatch<SetStateAction<boolean>>
) => {
    const {
        contentItemViewNodes,
        setIsTreeLoading,
        setArticlesLoading,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        setIsTreeLoading: state.actions.setIsTreeLoading,
        setArticlesLoading: state.actions.setArticlesLoading,
    }))

    const loadingNodesRef = useRef<Set<string>>(new Set())
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const addToLoadingState = useCallback(
        (nodeId: string) => {
            loadingNodesRef.current.add(nodeId)
            const newLoadingArray = [...Array.from(loadingNodesRef.current)]
            setArticlesLoading(newLoadingArray)
        },
        [setArticlesLoading]
    )

    const removeFromLoadingState = useCallback(() => {
        loadingNodesRef.current.clear()
        setArticlesLoading([])
    }, [setArticlesLoading])

    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: false,
    })

    const { getContentItemViewNodesAPI } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            // Signal that content loading is complete (resets coordination flag)
            setIsReadyForContentFetch(CONTENT_FETCH_STATES.FETCH_COMPLETE)
            removeFromLoadingState()
            await onContentViewFetchSuccess(response, [], false)
        },
        onError: () => {
            // Reset coordination flag on error to prevent stuck state
            setIsReadyForContentFetch(CONTENT_FETCH_STATES.FETCH_COMPLETE)
            removeFromLoadingState()
            setIsTreeLoading(false)
        },
    })

    const handleLatestContentItem = useCallback(
        async (trackChange: TrackedChange): Promise<boolean> => {
            const contentItemMapping = getContentItemViewNodeElementMapping(
                contentItemViewNodes
            )
            const contentItemId = trackChange.contentItemElementId
            const hasContentItem = Object.keys(contentItemMapping).some((key) =>
                key.includes(contentItemId)
            )
            if (hasContentItem) {
                return true
            }

            const rootContentItemId = trackChange.rootContentItemId
            const tab = trackChange?.partType?.toLowerCase() as TabName

            const isArticleLevel =
                trackChange.contentItemId === trackChange.rootContentItemId
            if (loadingNodesRef.current.has(rootContentItemId)) {
                return false
            }

            try {
                if (!isArticleLevel) {
                    timeoutRef.current = setTimeout(() => {
                        addToLoadingState(rootContentItemId)
                    }, 200)

                    await getContentItemViewNodesAPI(
                        [rootContentItemId],
                        tabIndex[tab]
                    )
                }
            } catch (error) {
                console.error('Error in handleLatestContentItem:', error)
                // Reset coordination flag on error to prevent stuck state
                setIsReadyForContentFetch(CONTENT_FETCH_STATES.FETCH_COMPLETE)
                removeFromLoadingState()
                setIsTreeLoading(false)
                return false
            }
            return false
        },
        [
            contentItemViewNodes,
            getContentItemViewNodesAPI,
            setIsTreeLoading,
            addToLoadingState,
            removeFromLoadingState,
            setIsReadyForContentFetch,
        ]
    )

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    return handleLatestContentItem
}
